import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import MediaGrid from '@/components/feature/MediaGrid';
import FilterBar from '@/pages/category/components/FilterBar';
import Pagination from '@/pages/category/components/Pagination';
import { useSeo } from '@/hooks/useSeo';
import { filterMedia } from '@/utils/media';

const PAGE_SIZE = 12;

const categoryMeta: Record<string, { label: string; desc: string }> = {
  movie: {
    label: '电影',
    desc: '汇集院线大作、高分剧情与类型佳作，涵盖动作、爱情、科幻、犯罪、武侠等题材。',
  },
  tv: {
    label: '电视剧',
    desc: '热播剧集持续更新，涵盖古装权谋、悬疑刑侦、都市爱情与历史传奇，追更不停。',
  },
  variety: {
    label: '综艺',
    desc: '真人秀、音乐、美食与旅行类综艺，轻松下饭，陪你度过每一个夜晚。',
  },
  all: {
    label: '全部影视',
    desc: '全站影视资源总览，支持按类型、地区与年份筛选，快速定位你想看的内容。',
  },
};

export default function CategoryPage() {
  const { type = 'all' } = useParams();
  const [params, setParams] = useSearchParams();

  const activeType = categoryMeta[type] ? type : 'all';
  const meta = categoryMeta[activeType];

  const genre = params.get('genre') ?? '全部';
  const region = params.get('region') ?? '全部';
  const year = params.get('year') ?? '全部';
  const sort = params.get('sort') ?? 'updated';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  useSeo({
    title: `${meta.label} - 黄色影视`,
    description: `${meta.label}频道：${meta.desc}`,
    keywords: `${meta.label},黄色影视,${meta.label}在线观看,高清${meta.label}`,
  });

  const filtered = useMemo(
    () => filterMedia({ type: activeType, genre, region, year, sort }),
    [activeType, genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, activeType, genre, region, year, sort]);

  const handleChange = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    const isDefault = value === '全部' || (key === 'sort' && value === 'updated');
    if (isDefault) next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const handleReset = () => setParams({}, { replace: true });

  const handlePage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setParams(next, { replace: true });
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-500">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{meta.label}</span>
        </nav>

        <header className="mt-4 border-b border-background-200/70 pb-6">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">{meta.label}</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.desc}</p>
        </header>

        <div className="mt-6">
          <FilterBar
            genre={genre}
            region={region}
            year={year}
            sort={sort}
            onChange={handleChange}
            onReset={handleReset}
          />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-semibold text-primary-500">{filtered.length}</span> 部作品
          </p>
        </div>

        <div className="mt-5">
          <MediaGrid
            items={paged}
            showType={activeType === 'all'}
            emptyText="暂无匹配影视资源，试试调整筛选条件"
            emptyAction={
              <button
                type="button"
                onClick={handleReset}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            }
          />
        </div>

        <Pagination page={currentPage} totalPages={totalPages} onChange={handlePage} />
      </div>
    </SiteLayout>
  );
}